<template>
  <b-modal v-model="internalShowModal" id="modal-center" centered :hide-footer="modalLoading" :hide-header="modalLoading"
    :no-close-on-backdrop="hasAnalyzeRight && !showDirectAnalyzeSelection" modal-class="analyze-modal new-modal">

    <!-- Modal Header -->
    <div slot="modal-header" :class="headerClass" class="d-flex w-100">
      <h2 v-if="hasAnalyzeRight && !showDirectAnalyzeSelection">{{ modalTitle }}</h2>
      <i class="icon-close custom-modal-close cursor-pointer" @click="closeModalHandler" />
    </div>

    <!-- Modal Content -->
    <template v-if="modalLoading">
      <div class="new-modal__loading">
        <div class="warning-area">
          <div class="loader-gif"></div>
        </div>
      </div>
    </template>
    <template v-else>
      <!-- Progress Bar -->
      <div v-if="hasAnalyzeRight && !showDirectAnalyzeSelection" class="modal-progress"
        :style="{ '--progress-width': progressPercentage }" />
      <!-- Upgrade Section -->
      <div v-if="showUpgradeSection" class="new-modal__upgrade">
        <img src="/images/upgrade.svg" loading="lazy" alt="upgrade" />
        <div class="warning-area">
          <div class="warning-area__head">
            {{ resource["candidateListAnalyzeModalUpgradeTitle"] }}
          </div>
          <div class="warning-area__note">
            {{ resource["candidateListAnalyzeModalUpgradeDescription"] }}
          </div>
        </div>
      </div>

      <div v-if="showDirectAnalyzeSelection" class="new-modal__upgrade">
        <img src="/images/upgrade.svg" loading="lazy" alt="upgrade" />
        <div class="warning-area">
          <div class="warning-area__head">
            {{ stepTitle }}
          </div>
          <div class="warning-area__note">
            {{ stepDescription }}
          </div>
          <language-dropdown v-if="hasAnalyzeRight" :className="'mb-4'" @setLanguage="setLanguage" />
        </div>
      </div>

      <!-- Step 1: Language Selection -->
      <div v-if="showStep1" class="new-modal__step">
        <img :src="stepImage" loading="lazy" alt="step" />
        <div class="warning-area">
          <div class="warning-area__head">
            {{ stepTitle }}
          </div>
          <div class="warning-area__note">
            {{ stepDescription }}
          </div>
        </div>
        <language-dropdown v-if="hasAnalyzeRight" :className="'mb-4'" @setLanguage="setLanguage" />
      </div>

      <!-- Step 2: Evaluation Criteria -->
      <div v-if="showStep2" class="new-modal__step">
        <interview-evaluation-criteria :use-min-criteria="true" :is-modal="true" :interview="interview" />
      </div>

      <!-- Modal Footer -->
      <div slot="modal-footer" :style="{ '--justify-content': footerJustifyContent }">
        <button v-if="showCancelButton" name="cancelBtn" @click="closeModalHandler"
          class="border-button-create-analyze">
          {{ resource["addCandidateModalCancelBtn"] }}
        </button>

        <button v-if="showUpgradeButton" @click="routePricing" name="upgradePlanBtn" class="bg-button-analyze">
          <i class="icon-star"></i>
          {{ resource['upgradePlanText'] }}
        </button>

        <button v-if="showBackButton" name="backBtn" @click="prevStep" class="border-button-create-analyze">
          {{ resource["addCandidateModalBackBtn"] }}
        </button>

        <button v-if="showNextButton" @click="nextStep" name="nextBtn" class="bg-button-analyze">
          {{ resource["addCandidateModalNextBtn"] }}
        </button>

        <button v-if="showSubmitButton" @click="handleSubmit" name="submitBtn" class="bg-button-analyze"
          :disabled="buttonLoading">
          <i v-if="buttonLoading" class="icon-spinner icon-spin" width="14" height="14" />
          {{ submitButtonText }}
        </button>

        <button v-if="showDirectAnalyzeSelectionButton" @click="handleSubmit" name="analyzeSelectionBtn"
          class="bg-button-analyze">
          <i v-if="buttonLoading" class="icon-spinner icon-spin" width="14" height="14" />
          {{ submitButtonText }}
        </button>
      </div>
    </template>
  </b-modal>
</template>
<script>
import ResourceManager from '@/services/resource.service';
import { languageItems } from '@/static/languageItems';
import hrvenueService from '../../services/hrvenue.service';
import { showNotification } from '../../static/helpers';
import InterviewEvaluationCriteria from '../hrVenue/interviewCreateEdit/interviewEvaluationCriteria.vue';
import LanguageDropdown from '../hrVenue/languageDropdown.vue';

export default {
  name: 'AnalyzeModal',
  components: {
    LanguageDropdown,
    InterviewEvaluationCriteria,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    rights: {
      type: Array,
      required: true,
    },
    analyzeOneCandidate: {
      type: Boolean,
      default: false,
    },
    completedCount: {
      type: Number,
      default: 0,
    },
    pageType: {
      type: String,
      required: true,
      default: 'interview-detail',
    },
    selectedCandidates: {
      type: Array,
      default: () => [],
    },
    interviewId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      internalShowModal: false,
      analyzeSelectedLanguage: null,
      buttonLoading: false,
      currentStep: 1,
      totalSteps: 2,
      interview: {
        id: null,
        selectedLanguages: [],
        selectedCriteria: [],
        criterias: [],
        isCheckedLanguageCheckBox: false,
        isCheckedLanguageDropdownError: false,
        tempPositionId: -1,
        tempJobDescription: '',
      },
      addOrUpdateCriteriaControl: -1,
      modalLoading: true,
      tempInterviewId: null,
    };
  },
  watch: {
    showModal(newVal) {
      if (newVal === true) {
        if (this.interviewId !== this.tempInterviewId) {
          this.tempInterviewId = this.interviewId;
          this.getAddOrUpdateCriteriaControl().then(() => {
            if (this.addOrUpdateCriteriaControl) {
              this.getInterviewDetails();
            }
          });
        }
      }
      this.internalShowModal = newVal;
    },
    internalShowModal(newVal) {
      if (!newVal) {
        this.$emit('closeModalHandler');
      }
    },
  },
  mounted() {
    let currentLanguage = null;
    if (typeof ResourceManager.CurrentLang !== 'string') {
      currentLanguage = ResourceManager.CurrentLang.id;
    } else {
      currentLanguage = languageItems.find(
        (item) => item.code === ResourceManager.CurrentLang,
      )?.id;
    }
    this.analyzeSelectedLanguage = currentLanguage;
    this.interview.id = this.interviewId;
  },
  computed: {
    hasAnalyzeRight() {
      const right = this.rights?.find((item) => item.companyRightType === 4);
      return right?.totalUsableRight > 0;
    },

    progressPercentage() {
      return `${(this.currentStep / this.totalSteps) * 100}%`;
    },

    headerClass() {
      return this.hasAnalyzeRight && !this.showDirectAnalyzeSelection ? 'justify-content-between' : 'justify-content-end';
    },

    modalTitle() {
      return this.analyzeOneCandidate
        ? this.resource.candidateListAnalyzeModalTitleOne
        : this.resource.candidateListAnalyzeModalTitle;
    },

    stepImage() {
      return this.hasAnalyzeRight ? '/images/interview-list-ai.svg' : '/images/upgrade.svg';
    },

    stepTitle() {
      if (this.pageType === 'interview-detail') {
        return this.analyzeOneCandidate
          ? this.resource.candidateListAnalyzeModalTitleOne
          : this.resource.candidateListAnalyzeModalTitle;
      }
      return this.resource.interviewListAnalyzeModalTitle.replace(
        '{candidateCompletedCount}',
        this.completedCount,
      );
    },

    stepDescription() {
      if (this.pageType === 'interview-detail') {
        return this.analyzeOneCandidate
          ? this.resource.candidateListAnalyzeModalDescriptionOne
          : this.resource.candidateListAnalyzeModalDescription;
      }
      return this.resource.interviewListAnalyzeModalDescription;
    },

    submitButtonText() {
      return this.pageType === 'interview-detail'
        ? this.resource.candidateListAnalyzeModalSubmitBtn
        : this.resource.interviewListAnalyzeModalSubmitBtn;
    },

    footerJustifyContent() {
      return this.hasAnalyzeRight ? 'flex-end' : 'space-between';
    },

    showUpgradeSection() {
      return !this.hasAnalyzeRight && this.currentStep === 1;
    },

    showDirectAnalyzeSelection() {
      return this.hasAnalyzeRight && !this.addOrUpdateCriteriaControl;
    },

    showStep1() {
      return this.hasAnalyzeRight && this.currentStep === 1 && this.addOrUpdateCriteriaControl;
    },

    showStep2() {
      return this.currentStep === 2 && this.addOrUpdateCriteriaControl;
    },

    showCancelButton() {
      return this.currentStep === 1;
    },

    showUpgradeButton() {
      return !this.hasAnalyzeRight;
    },

    showBackButton() {
      return this.hasAnalyzeRight && this.currentStep === 2 && this.addOrUpdateCriteriaControl;
    },

    showNextButton() {
      return this.hasAnalyzeRight && this.currentStep === 1 && this.addOrUpdateCriteriaControl;
    },

    showSubmitButton() {
      return this.hasAnalyzeRight && this.currentStep === 2 && this.addOrUpdateCriteriaControl;
    },

    showDirectAnalyzeSelectionButton() {
      return this.hasAnalyzeRight && this.currentStep === 1 && !this.addOrUpdateCriteriaControl;
    },
  },
  methods: {
    async getInterviewDetails() {
      try {
        const {
          interview,
        } = await hrvenueService.getInterview(this.interviewId);
        this.interview = {
          ...interview,
          selectedLanguages: [],
          selectedCriteria: [],
          criterias: [],
          isCheckedLanguageCheckBox: false,
          isCheckedLanguageDropdownError: false,
          tempPositionId: interview.position?.value?.id || -1,
          position: {
            id: interview.positionId || null,
            value: interview.positionId || '',
          } || null,
          tempJobDescription: '',
          jobDescription: interview.jobDescription || '',
        };
      } catch (error) {
        showNotification(this.resource['label.Error'], error.message);
      }
    },
    async getAddOrUpdateCriteriaControl() {
      this.modalLoading = true;
      this.interview = {
        id: null,
        selectedLanguages: [],
        selectedCriteria: [],
        criterias: [],
        isCheckedLanguageCheckBox: false,
        isCheckedLanguageDropdownError: false,
        tempPositionId: -1,
        tempJobDescription: '',
      };
      const response = await hrvenueService.getAddOrUpdateCriteriaControl(this.interviewId);
      this.addOrUpdateCriteriaControl = response === true;
      this.modalLoading = false;
    },

    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep += 1;
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },

    closeModalHandler() {
      this.internalShowModal = false;
      this.currentStep = 1;
    },

    setLanguage(language) {
      this.analyzeSelectedLanguage = language.id;
    },

    async handleSubmit() {
      if (!this.showDirectAnalyzeSelection && this.interview.selectedLanguages?.length === 0 && this.interview.isCheckedLanguageCheckBox) {
        showNotification(
          this.resource['label.Error'],
          this.resource['hrvenue.InterviewEdit.PleaseSelectLanguageMessage'],
          'error',
        );
        this.interview.isCheckedLanguageDropdownError = true;
        return false;
      }

      const totalCriteria = this.interview.selectedLanguages?.length + this.interview.selectedCriteria?.length;

      if (!this.showDirectAnalyzeSelection && totalCriteria < 1) {
        showNotification(
          this.resource['Label.Error'],
          this.resource['hrvenue.evaluationCriteriaMinError'],
          'error',
        );
        return false;
      }
      if (!this.showDirectAnalyzeSelection && totalCriteria > 7) {
        showNotification(
          this.resource['Label.Error'],
          this.resource['hrvenue.evaluationCriteriaMaxError'],
          'error',
        );
        return false;
      }
      this.interview.selectedLanguages = this.interview.selectedLanguages.map((criteria) => ({
        ...criteria,
        criteriaId: '',
      }));
      if (this.addOrUpdateCriteriaControl) {
        const allCriterias = this.interview.selectedLanguages.concat(this.interview.selectedCriteria);
        await hrvenueService.addOrUpdateInterviewCriteria(this.interviewId, null, allCriterias).then(() => {
          this.startAnalyzeAllCandidates(2);
        });
        return false;
      }
      this.startAnalyzeAllCandidates(2);
    },

    async startAnalyzeAllCandidates(analyzeWorkOrderType) {
      this.internalShowModal = false;
      //   dataLayer.push({
      //     event: 'ga4_event',
      //     event_name: 'popup_click',
      //     page_type: 'interview-detail',
      //     site_type: 'interview',
      //     event_parameters: {
      //       label: 'continue',
      //       popup_name: 'analyze-your-interviews',
      //     },
      //   });

      this.buttonLoading = true;
      try {
        const response = await hrvenueService.startAnalyze(
          analyzeWorkOrderType,
          this.pageType === 'interview-detail' ? this.selectedCandidates.length === 0 : true,
          this.interviewId,
          this.pageType === 'interview-detail' ? this.selectedCandidates.map((item) => item.id) : null,
          false,
          this.analyzeSelectedLanguage,
          this.interview.selectedLanguages.concat(this.interview.selectedCriteria),
        );

        if (!response.body) {
          if (analyzeWorkOrderType === 2) {
            showNotification(
              this.resource['label.Success'],
              this.resource.candidateDetailAnalyzeAndSummaryStartNotification,
              'success',
            );
          }
          this.selectedCandidates = [];
          this.currentStep = 1;
        }

        this.closeModalHandler();
      } catch (error) {
        showNotification(this.resource['label.Error'], error.message);
      } finally {
        this.buttonLoading = false;
      }
    },
    routePricing() {
      this.trackUpgradeClick();
      const lang = this.$route.params.lang === 'en' ? '' : this.$route.params.lang;
      this.$router.push({ name: 'PricingListView', params: { lang } });
    },

    trackUpgradeClick() {
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({
          event: 'ga4_event',
          event_name: 'popup_click',
          page_type: this.pageType,
          site_type: 'interview',
          event_parameters: {
            label: 'upgrade-plan',
            popup_name: 'analyze-your-interviews',
          },
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~@/style/starter.scss";

.warning-area {
  text-align: center;

  &__head {
    color: #353535;
    font-family: Roboto;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    margin-bottom: 8px;
  }

  &__note {
    color: #858585;
    font-family: Roboto;
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
  }
}

.modal-progress {
  height: 4px;
  background-color: #D9D9D9;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: var(--progress-width, 0%);
    background-color: #ff8d06;
    transition: width 0.3s ease;
  }
}

.loader-gif {
  width: 40px;
  padding: 5px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #ff8d06;
  --_m: conic-gradient(#0000 25%, #000), linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m);
  mask: var(--_m);
  -webkit-mask-composite: source-out;
  mask-composite: subtract;
  animation: l3 1s infinite linear;

  &--small {
    width: 14px;
    background: #fff;
  }
}
@keyframes l3 {
  to {
    transform: rotate(1turn);
  }
}

::v-deep .new-modal {
  .language-dropdown-container {
    width: 400px;
    margin-top: 24px;
  }

  .modal-dialog {
    max-width: fit-content;
  }

  .modal-content {
    border-radius: 12px;
    border: none;
  }

  .modal-header {
    padding: 16px 16px 16px 32px;
    border-bottom: var(--border-bottom);

    @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
      padding: 16px;
    }

    i {
      font-size: 24px;
      color: #333333;
    }

    h2 {
      color: #333333;
      font-family: Roboto;
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
      /* 137.5% */
    }
  }

  .modal-body {
    border-bottom: 0;
    padding: 0;
    height: auto;
    max-height: none;
  }

  .px-64 {
    padding: 0 64px;
  }

  &__step {
    width: 560px;
    height: 606px;
    padding: 24px 16px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    overflow: overlay;
    scrollbar-gutter: stable both-edges;
    &::-webkit-scrollbar {
        width: 7px !important;
    }
    &::-webkit-scrollbar-track {
        background-color: #fff !important;
        border-radius: 2px !important;
    }
    &::-webkit-scrollbar-thumb {
        border-radius: 2px !important;
        background: linear-gradient(to right, #D8D8D8 3px, #FFF 4px) !important;
    }

    img {
      margin-bottom: 14px;
    }

    &:has(.evaluation-criteria) {
      justify-content: flex-start;
      margin-bottom: 48px;
    }

    .warning-area {
      width: 400px;
    }
  }

  &__upgrade,
  &__loading {
    width: 446px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 24px 24px;

    img {
      margin-bottom: 14px;
    }
  }

  &__loading {
    min-height: 400px;
  }

  .modal-footer {
    padding: 0 32px 16px;

    div {
      display: flex;
      justify-content: var(--justify-content);
      align-items: center;
      gap: 8px;
      width: 100%;
    }
  }

  &:has(.new-modal__upgrade) {

    .border-button-create-analyze,
    .bg-button-analyze {
      flex: 1 !important;
    }
  }

  &:has(.new-modal__step) {
    .modal-footer {
      border-top: 1px solid #E5E5E5 !important;
      padding: 16px 32px !important;
    }
  }

  .border-button-create-analyze {
    display: flex;
    width: 132px;
    height: 36px;
    padding: 9px 8px;
    justify-content: center;
    align-items: center;
    border-radius: 40px;
    border: 1px solid #ff8d06;
    background: transparent !important;

    color: var(--main-colors-orange, #ff8d06);
    text-align: center;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;

    &:hover {
      background: #ff8d06 !important;
      color: #fff !important;
    }
  }

  .bg-button-analyze {
    display: flex;
    width: 132px;
    height: 36px;
    padding: 9px 8px;
    justify-content: center;
    align-items: center;
    border-radius: 40px;
    border: 1px solid #ff8d06;
    background: #ff8d06;

    color: #fff;
    text-align: center;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;

    &:hover {
      background: #FF7415 !important;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
